import React, { useEffect, useState } from 'react';
import { FaAndroid, FaApple, FaEdit, FaExternalLinkAlt, FaPlus, FaSearch, FaSort, FaSortDown, FaSortUp, FaTrash } from 'react-icons/fa';
import { useAuth } from '../../provider/auth.provider';
import { Pagination } from '../../utils/pagination.utils';
import { AppVersion, AppVersionPagination } from './types';
import { useDebouncedCallback } from '../../hooks/useDebounce';

interface AppVersionListProps {
  appVersions: AppVersion[];
  pagination: AppVersionPagination;
  onSearch: (search: string) => void;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  onAddVersion: () => void;
  onEditVersion: (v: AppVersion) => void;
  onDeleteVersion: (v: AppVersion) => void;
  onSort: (field: string, direction: 'ASC' | 'DESC') => void;
  loading?: boolean;
  searchValue?: string;
  sortState?: { field: string; direction: 'ASC' | 'DESC' };
}

export const AppVersionList: React.FC<AppVersionListProps> = ({
  appVersions,
  pagination,
  onSearch,
  onPageChange,
  onLimitChange,
  onAddVersion,
  onEditVersion,
  onDeleteVersion,
  onSort,
  loading = false,
  searchValue = '',
  sortState,
}) => {
  const { hasPermission } = useAuth();
  const [searchTerm, setSearchTerm] = useState(searchValue);
  const [sortConfig, setSortConfig] = useState(sortState || { field: 'created_at', direction: 'DESC' as 'DESC' });
  const debouncedOnSearch = useDebouncedCallback(onSearch, 400);

  useEffect(() => { setSearchTerm(searchValue || ''); }, [searchValue]);
  useEffect(() => { if (sortState) setSortConfig(sortState); }, [sortState]);

  const handleSort = (field: string) => {
    const direction = sortConfig.field === field && sortConfig.direction === 'ASC' ? 'DESC' : 'ASC';
    setSortConfig({ field, direction: direction as 'ASC' | 'DESC' });
    onSort(field, direction as 'ASC' | 'DESC');
  };

  const getSortIcon = (field: string) => {
    if (sortConfig.field !== field) return <FaSort className="w-3 h-3" />;
    return sortConfig.direction === 'ASC' ? <FaSortUp className="w-3 h-3" /> : <FaSortDown className="w-3 h-3" />;
  };

  const formatDate = (value: string) =>
    new Date(value).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });

  return (
    <div className="bg-white shadow-sm rounded overflow-hidden border border-gray-200">
      {/* Header */}
      <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <h2 className="text-xl font-semibold text-gray-900">App Version Management</h2>
          {hasPermission('AppVersion', 'create') && (
            <button
              onClick={onAddVersion}
              className="bg-blue-800 hover:bg-blue-950 text-white px-4 py-2 rounded flex items-center gap-2 transition-colors text-sm font-medium shadow-sm"
            >
              <FaPlus className="w-4 h-4" />
              Add Version
            </button>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gray-50/30">
        <div className="max-w-md">
          <label className="block text-xs font-medium text-gray-700 mb-1">Search Versions</label>
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); debouncedOnSearch(e.target.value); }}
              placeholder="Search by version or remarks"
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider min-w-[100px]">Actions</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('version')}>
                <div className="flex items-center gap-1">Version {getSortIcon('version')}</div>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Platform</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('release_date')}>
                <div className="flex items-center gap-1">Release Date {getSortIcon('release_date')}</div>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">App URL</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Deadline (Days)</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Changes</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Remarks</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('created_at')}>
                <div className="flex items-center gap-1">Created {getSortIcon('created_at')}</div>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={10} className="px-4 py-10 text-center">
                  <div className="flex flex-col items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-800 mb-3" />
                    <span className="text-sm text-gray-600">Loading versions...</span>
                  </div>
                </td>
              </tr>
            ) : appVersions.length === 0 ? (
              <tr>
                <td colSpan={10} className="px-4 py-10 text-center">
                  <p className="text-sm text-gray-500">No app versions found</p>
                  <p className="text-xs text-gray-400 mt-1">Try adjusting your search</p>
                </td>
              </tr>
            ) : (
              appVersions.map((v) => (
                <tr key={v.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-1">
                      {hasPermission('AppVersion', 'edit') && (
                        <button onClick={() => onEditVersion(v)} className="text-blue-700 hover:text-blue-900 p-2 hover:bg-blue-50 rounded transition-colors" title="Edit">
                          <FaEdit className="w-4 h-4" />
                        </button>
                      )}
                      {hasPermission('AppVersion', 'delete') && (
                        <button onClick={() => onDeleteVersion(v)} className="text-red-500 hover:text-red-700 p-2 hover:bg-red-50 rounded transition-colors" title="Delete">
                          <FaTrash className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-sm font-semibold text-gray-900 font-mono">{v.version}</span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${v.platform === 'android' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-gray-100 text-gray-700 border border-gray-200'}`}>
                      {v.platform === 'android' ? <FaAndroid className="w-3 h-3" /> : <FaApple className="w-3 h-3" />}
                      {v.platform.charAt(0).toUpperCase() + v.platform.slice(1)}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-700">{formatDate(v.release_date)}</span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    {v.app_url ? (
                      <a href={v.app_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-blue-700 hover:text-blue-900 text-sm">
                        <FaExternalLinkAlt className="w-3 h-3" /> Open
                      </a>
                    ) : (
                      <span className="text-gray-400 text-sm italic">—</span>
                    )}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    {v.deadline_days != null ? (
                      <span className="text-sm text-gray-700 font-medium">{v.deadline_days} days</span>
                    ) : (
                      <span className="text-gray-400 text-sm italic">—</span>
                    )}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${v.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {v.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-4 max-w-xs">
                    {v.changes_done?.length ? (
                      <ul className="list-disc list-inside space-y-0.5">
                        {v.changes_done.slice(0, 3).map((c, i) => (
                          <li key={i} className="text-xs text-gray-700 truncate">{c}</li>
                        ))}
                        {v.changes_done.length > 3 && (
                          <li className="text-xs text-gray-400">+{v.changes_done.length - 3} more</li>
                        )}
                      </ul>
                    ) : (
                      <span className="text-gray-400 text-sm italic">—</span>
                    )}
                  </td>
                  <td className="px-4 py-4 max-w-xs">
                    <span className="text-sm text-gray-600 truncate block">{v.remarks || '—'}</span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-700">{formatDate(v.created_at)}</span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination pagination={pagination} onPageChange={onPageChange} onLimitChange={onLimitChange} />
    </div>
  );
};
